import { useMemo, useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDeleteRight,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';

import {
  Badge,
  Button,
  ButtonText,
  ClearButton,
  SearchInput,
  Table
} from '@morressier/alembic';

import {
  fetchSubmissionsDashBoardConfig,
  fetchEmailHistoryList,
  IEmailHistoryItem
} from 'src/api/queries';
import { ClampedTooltipText } from 'src/components/shared/ClampedTooltipText';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { formatDateAndTime } from 'src/utils/date';
import { renderRecipient } from 'src/utils/email.utils';

import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import EmailDetailsOverlay from './EmailDetailsOverlay';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import { useNavigate, useLocation } from 'react-router-dom';

type EmailLogFilterKey = 'submissionContextId' | 'eventNames';

type EmailLogFilters = Record<EmailLogFilterKey, string[]>;

const defaultValues: EmailLogFilters = {
  submissionContextId: [],
  eventNames: []
};

const getDisplayLabel = (value?: string) => {
  if (!value) return 'NA';
  const label = value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const EmailLogsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [selectedLog, setSelectedLog] = useState<IEmailHistoryItem | null>(
    null
  );
  const [sort, setSort] = useState<SortField | null>();

  const { watch, setValue, reset } = useForm<EmailLogFilters>({
    defaultValues
  });
  const filters = watch();

  const {
    workspaceId,
    workspaceShortname,
    submissionContextId: fallbackSubmissionContextId
  } = useWorkspace();

  const view = 'email-logs';
  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { workspaceId, view }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ workspaceId, view }),
    enabled: !!workspaceId
  });

  const filterGroups = useMemo(
    () =>
      dashBoardConfigs?.filters?.map((filter: any) => ({
        ...filter,
        items: filter.items ?? []
      })) ?? [],
    [dashBoardConfigs?.filters]
  );

  const trimmedSearchQuery = searchQuery.trim();
  const selectedSubmissionContextIds = filters.submissionContextId ?? [];
  const selectedEvents = filters.eventNames ?? [];

  const isEmail =
    trimmedSearchQuery.includes('@') &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSearchQuery);

  const emailSearch = isEmail ? trimmedSearchQuery : undefined;
  const apiSearch =
    !isEmail && trimmedSearchQuery ? trimmedSearchQuery : undefined;

  const offset = (page - 1) * pageSize;

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'email-history-list',
      {
        limit: pageSize,
        offset,
        search: apiSearch,
        email: emailSearch,
        submissionContextId: selectedSubmissionContextIds,
        eventNames: selectedEvents,
        sort: sort ?? undefined
      }
    ],
    queryFn: () =>
      fetchEmailHistoryList({
        limit: pageSize,
        offset,
        search: apiSearch,
        email: emailSearch,
        submissionContextId: selectedSubmissionContextIds.length
          ? selectedSubmissionContextIds
          : undefined,
        eventNames: selectedEvents.length ? selectedEvents : undefined,
        sort: sort ?? undefined
      })
  });

  const logs: IEmailHistoryItem[] = data?.data ?? [];

  const totalItems = data?.total ?? 0;
  const startIndex = totalItems === 0 ? 0 : offset + 1;
  const endIndex = Math.min(page * pageSize, totalItems);

  const hasFilters =
    !!searchQuery ||
    !!selectedSubmissionContextIds.length ||
    !!selectedEvents.length;

  const handleFiltersSubmit = (
    newSelectedFilters: Record<string, string[]>
  ) => {
    reset({ ...defaultValues, ...newSelectedFilters });

    setSelectedFilters(newSelectedFilters);

    setPage(1);
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: EmailLogFilterKey
  ) => {
    const updatedFilter = filters[fieldName].filter(
      currentFilter => currentFilter !== filterToRemove
    );

    setValue(fieldName, updatedFilter);
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setPage(1);
  };

  const clearFilters = () => {
    reset(defaultValues);
    setSelectedFilters({});
    setSearchValue('');
    setSearchQuery('');
    setPage(1);
  };

  const handleSortApply = (selectedSort: SortField | null) => {
    setSort(selectedSort);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const handleOpenSubmission = (log: IEmailHistoryItem) => {
    const currentSubmissionId = log.submissionId;
    const currentSubmissionContextId =
      log.submissionContextId || fallbackSubmissionContextId;

    if (
      !workspaceShortname ||
      !currentSubmissionId ||
      !currentSubmissionContextId
    )
      return;

    navigate(
      `/app/workspace/${workspaceShortname}/j/${currentSubmissionContextId}/submissions?selectedItem=${currentSubmissionId}&activeTab=overview`,
      {
        state: { from: location.pathname + location.search }
      }
    );
  };

  return (
    <Flex direction="column" gap={4} width="100%" mt={3}>
      <Flex
        gap={4}
        alignItems={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        width="100%"
      >
        <Flex gap={2} alignItems="center" flex="1">
          <SearchInput
            placeholder="Search by Recipient Email, Subject or Manuscript ID"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            width="100%"
            name="emailLogSearch"
            value={searchValue}
            enterKeyHint="search"
            onChange={e => setSearchValue(e.currentTarget.value)}
            onInputClear={() => {
              setSearchValue('');
              setSearchQuery('');
              setPage(1);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchQuery(searchValue.trim());
                setPage(1);
              }
            }}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchQuery(searchValue.trim());
                setPage(1);
              }
            }}
          />
          <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
            <Button
              variant="primary"
              aria-label="Search email logs"
              onClick={() => {
                setSearchQuery(searchValue.trim());
                setPage(1);
              }}
            >
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </Box>
        </Flex>
        <FilterDrawer
          filterGroups={filterGroups}
          watch={watch as any}
          onSubmit={handleFiltersSubmit}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      </Flex>

      <Flex alignItems="center" gap={2} wrap="wrap">
        {isLoading ? (
          <Text textStyle="textBody">Searching for matches...</Text>
        ) : (
          <Text textStyle="textBody">
            {totalItems > 0 ? (
              <>
                Showing <strong>{startIndex}</strong> -{' '}
                <strong>{endIndex}</strong> of <strong>{totalItems}</strong>{' '}
                emails logged.
              </>
            ) : (
              <>
                Showing <strong>0</strong> emails logged.
              </>
            )}
          </Text>
        )}

        {hasFilters && (
          <ButtonText
            onClick={clearFilters}
            leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
            variant="secondary"
          >
            Clear filters
          </ButtonText>
        )}

        <Box
          flex="1"
          height="1px"
          backgroundColor="borderSubtle"
          display={{ base: 'none', lg: 'block' }}
        />

        {dashBoardConfigs?.sortOptions && (
          <Box width={{ base: '100%', lg: 'auto' }}>
            <SortMenu
              appliedSort={sort ?? null}
              options={dashBoardConfigs.sortOptions}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          </Box>
        )}
      </Flex>

      {Object.entries(filters).some(([, value]) => value.length > 0) && (
        <Flex alignItems="center" gap={2} wrap="wrap">
          {filterGroups.map(filterConfig => {
            const filterKey = filterConfig.key as EmailLogFilterKey;
            const filterValues = filters[filterKey] ?? [];

            if (!filterValues.length) return null;

            return filterValues.map(value => {
              const label =
                filterConfig.items.find(
                  (item: { value: string; name: string }) =>
                    item.value === value
                )?.name ?? value;

              return (
                <Badge
                  key={`${filterKey}-${value}`}
                  size="base"
                  variant="interactive"
                >
                  <Flex alignItems="center" gap={2}>
                    {label}
                    <FontAwesomeIcon
                      icon={faXmark}
                      cursor="pointer"
                      onClick={() => removeSingleFilter(value, filterKey)}
                    />
                  </Flex>
                </Badge>
              );
            });
          })}
        </Flex>
      )}

      {isLoading ? (
        <SubmissionLoader
          title="Loading Email Logs"
          subTitle="We are retrieving email history logs. This should only take a moment."
          bgColor="neutralSecondaryFill"
        />
      ) : isError ? (
        <SubmissionLoader
          title="Failed to Load Email Logs"
          subTitle="There was an error loading email history logs. Please try again later."
          bgColor="errorPrimaryFill"
        />
      ) : logs.length === 0 ? (
        <SubmissionLoader
          title="No Email Logs Found"
          subTitle="There are no email logs matching your current filters."
          bgColor="neutralSecondaryFill"
        />
      ) : (
        <Box width="100%" overflowX="auto">
          <Box minWidth="1160px">
            <Table>
              <Table.HeaderRow gap={2}>
                <Table.HeaderRow gap={2}>
                  <Table.HeaderCell flex="0.4">#</Table.HeaderCell>
                  <Table.HeaderCell flex="1.7">
                    Recipient Email
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="2">Subject</Table.HeaderCell>
                  <Table.HeaderCell flex="1.2">Manuscript ID</Table.HeaderCell>
                  <Table.HeaderCell flex="1.2">Event Name</Table.HeaderCell>
                  <Table.HeaderCell flex="1.2">Event Time</Table.HeaderCell>
                </Table.HeaderRow>
              </Table.HeaderRow>
              <Table.Body>
                {logs.map((log: IEmailHistoryItem, index: number) => {
                  const formattedDate = log.createdAt
                    ? formatDateAndTime(new Date(log.createdAt))
                    : '--';

                  const rowNumber = (page - 1) * pageSize + index + 1;

                  const submissionId = log.submissionId;
                  const submissionContextId =
                    log.submissionContextId || fallbackSubmissionContextId;
                  const manuscriptIdLabel = log.manuscriptId || '--';

                  const canOpenSubmission =
                    !!workspaceShortname &&
                    !!submissionId &&
                    !!submissionContextId &&
                    manuscriptIdLabel !== 'NA' &&
                    manuscriptIdLabel !== '--';

                  return (
                    <Table.Row
                      key={log._id}
                      gap={2}
                      _last={{ borderBottom: 'none' }}
                    >
                      <Table.RowCell
                        flex="0.4"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <Text textStyle="textBody">{rowNumber}</Text>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.7"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <ClearButton
                          ariaLabel="Open email details"
                          justifyContent="flex-start"
                          height="auto"
                          width="100%"
                          minWidth="0"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Text
                            textStyle="headingItem"
                            color="actionPrimaryFill"
                            textAlign="left"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="anywhere"
                          >
                            {renderRecipient(log.recipients?.to) || '--'}
                          </Text>
                        </ClearButton>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="2"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <ClampedTooltipText value={log.subject ?? '--'} />
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.2"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        {canOpenSubmission ? (
                          <ClearButton
                            ariaLabel={`Open submission ${manuscriptIdLabel}`}
                            justifyContent="flex-start"
                            height="auto"
                            width="100%"
                            minWidth="0"
                            onClick={() => handleOpenSubmission(log)}
                          >
                            <Text
                              textStyle="headingItem"
                              color="actionPrimaryFill"
                              textAlign="left"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              overflowWrap="anywhere"
                            >
                              {manuscriptIdLabel}
                            </Text>
                          </ClearButton>
                        ) : (
                          <Text
                            textStyle="textBody"
                            whiteSpace="normal"
                            wordBreak="break-word"
                          >
                            {manuscriptIdLabel}
                          </Text>
                        )}
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.2"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <Text
                          textStyle="textBody"
                          whiteSpace="normal"
                          wordBreak="break-word"
                        >
                          {getDisplayLabel(log.eventName)}
                        </Text>
                      </Table.RowCell>
                      <Table.RowCell flex="1.2" paddingY={3}>
                        <Text textStyle="textBody">{formattedDate}</Text>
                      </Table.RowCell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Box>
        </Box>
      )}

      <TablePagination
        ariaLabel="Email logs pagination"
        currentPage={page}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {selectedLog && (
        <EmailDetailsOverlay
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </Flex>
  );
};

export default EmailLogsTable;
